import { useTranslation } from '@/store/languageStore';
import type { CrowdSecEvent } from '@/services/sysadmin/securityService';

function formatWhen(iso: string): string {
  if (!iso) return '–';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function scenarioKey(scenario: string): string {
  return scenario.replace(/^crowdsecurity\//, '');
}

interface CrowdSecEventsTableProps {
  events: CrowdSecEvent[];
  localBanCount: number;
  installed: boolean;
  active: boolean;
  busyIp: string | null;
  onUnban: (ip: string) => void;
}

export function CrowdSecEventsTable({
  events,
  localBanCount,
  installed,
  active,
  busyIp,
  onUnban,
}: CrowdSecEventsTableProps) {
  const { t } = useTranslation();

  const reasonLabel = (scenario: string) => {
    if (!scenario) return '–';
    const key = `sysAdmin.security.scenario.${scenarioKey(scenario)}`;
    const label = t(key);
    return label === key ? scenarioKey(scenario).replace(/-/g, ' ') : label;
  };

  const activeBans = events.filter((row) => row.active);
  const alerts = events.filter((row) => !row.active);
  const shownBanCount = Math.max(localBanCount, activeBans.length);

  const renderRows = (rows: CrowdSecEvent[]) =>
    rows.map((row, index) => (
      <tr key={`${row.id || row.ip}-${row.createdAt}-${index}`} className="border-b border-border align-top">
        <td className="py-1.5 pr-3 whitespace-nowrap">{formatWhen(row.createdAt)}</td>
        <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{row.ip || '–'}</td>
        <td className="py-1.5 pr-3">{reasonLabel(row.scenario)}</td>
        <td className="py-1.5 pr-3 whitespace-nowrap">{row.country || '–'}</td>
        <td className="py-1.5 pr-3 text-textSecondary max-w-[16rem] truncate" title={row.asName}>
          {row.asName || '–'}
        </td>
        <td className="py-1.5 pr-3 whitespace-nowrap">
          {row.active
            ? row.duration || t('sysAdmin.security.banActive')
            : row.action === 'ban'
              ? t('sysAdmin.security.banExpired')
              : t('sysAdmin.security.alertOnly')}
        </td>
        <td className="py-1.5 text-right whitespace-nowrap">
          {row.active && row.ip ? (
            <button
              type="button"
              disabled={busyIp === row.ip}
              onClick={() => onUnban(row.ip)}
              className="text-primary text-xs underline disabled:opacity-50"
            >
              {t('sysAdmin.security.unban')}
            </button>
          ) : null}
        </td>
      </tr>
    ));

  const tableHead = (
    <thead>
      <tr className="text-left text-textSecondary text-xs border-b border-border">
        <th className="py-1 pr-3">{t('sysAdmin.security.colWhen')}</th>
        <th className="py-1 pr-3">{t('sysAdmin.security.colIp')}</th>
        <th className="py-1 pr-3">{t('sysAdmin.security.colReason')}</th>
        <th className="py-1 pr-3">{t('sysAdmin.security.colCountry')}</th>
        <th className="py-1 pr-3">{t('sysAdmin.security.colDetail')}</th>
        <th className="py-1 pr-3">{t('sysAdmin.security.colUntil')}</th>
        <th className="py-1" />
      </tr>
    </thead>
  );

  return (
    <div className="border border-border rounded-lg p-4 bg-bgElevated">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h2 className="font-medium">{t('sysAdmin.security.crowdsec')}</h2>
        <p className="text-sm text-textSecondary">
          {installed
            ? active
              ? t('sysAdmin.security.active')
              : t('sysAdmin.security.inactive')
            : t('sysAdmin.security.crowdsecMissing')}
          {installed && active ? ` · ${t('sysAdmin.security.localBans', { count: shownBanCount })}` : ''}
        </p>
      </div>
      <p className="text-sm text-textSecondary mb-3">{t('sysAdmin.security.eventsHint')}</p>
      {events.length === 0 && shownBanCount === 0 ? (
        <p className="text-sm text-textTertiary">{t('sysAdmin.security.noEvents')}</p>
      ) : events.length === 0 && shownBanCount > 0 ? (
        <p className="text-sm text-amber-800 dark:text-amber-200">{t('sysAdmin.security.bansMissingInList')}</p>
      ) : (
        <div className="space-y-4">
          {activeBans.length > 0 ? (
            <div className="overflow-x-auto">
              <p className="text-xs font-medium text-textSecondary mb-1">{t('sysAdmin.security.activeBansHeading')}</p>
              <table className="w-full text-sm">
                {tableHead}
                <tbody>{renderRows(activeBans)}</tbody>
              </table>
            </div>
          ) : shownBanCount > 0 ? (
            <p className="text-sm text-amber-800 dark:text-amber-200">{t('sysAdmin.security.bansMissingInList')}</p>
          ) : null}
          {alerts.length > 0 ? (
            <div className="overflow-x-auto">
              <p className="text-xs font-medium text-textSecondary mb-1">{t('sysAdmin.security.recentAlertsHeading')}</p>
              <table className="w-full text-sm">
                {tableHead}
                <tbody>{renderRows(alerts)}</tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
